import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2, Clock, Inbox, Loader2, RotateCw, FileWarning, Link2, UserCog, History, Trash2, FileCheck, ShieldCheck, XCircle } from "lucide-react";
import ReconcileModal from "@/components/admin/ReconcileModal";
import ReassignModal from "@/components/admin/ReassignModal";
import AuditDrawer from "@/components/admin/AuditDrawer";
import LegacyCleanupModal from "@/components/admin/LegacyCleanupModal";
import AdminActivityWidget from "@/components/admin/AdminActivityWidget";
import PaOutcomeModal from "@/components/admin/PaOutcomeModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COPY = {
  seeded: { label: "Seeded — awaiting RN", cls: "bg-[#FFFDEC] text-[#8A6B00] border-[#F0E29A]", icon: Inbox },
  claimed: { label: "Claimed — in progress", cls: "bg-[#E7F1FB] text-[#0A3D79] border-[#B7CADA]", icon: Clock },
  complete: { label: "Complete (local)", cls: "bg-[#E9F5EE] text-[#0E6B3F] border-[#B5D9C3]", icon: CheckCircle2 },
  submitted_to_avida: { label: "Synced to Avida", cls: "bg-[#E9F5EE] text-[#0E6B3F] border-[#B5D9C3]", icon: CheckCircle2 },
  avida_sync_failed: { label: "Sync failed", cls: "bg-[#FBECEC] text-[#A42A2A] border-[#E6B8B8]", icon: AlertTriangle },
  in_flight: { label: "Retrying…", cls: "bg-[#FFF6E0] text-[#8A6B00] border-[#F0E29A]", icon: Loader2 },
  completed_standalone: { label: "Awaiting reconcile", cls: "bg-[#FFF6E0] text-[#8A6B00] border-[#F0E29A]", icon: FileWarning },
  reconciled: { label: "Reconciled", cls: "bg-[#E9F5EE] text-[#0E6B3F] border-[#B5D9C3]", icon: Link2 },
};

function StatusBadge({ status, syncFailed, inFlight }) {
  let effective;
  if (inFlight) effective = "in_flight";
  else if (syncFailed) effective = "avida_sync_failed";
  else effective = status;
  const meta = STATUS_COPY[effective] || STATUS_COPY.seeded;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}
      data-testid={`draft-status-${effective}`}
    >
      <Icon className={`w-3 h-3 ${effective === "in_flight" ? "animate-spin" : ""}`} />
      {meta.label}
    </span>
  );
}

function fmtCountdown(ms) {
  if (ms <= 0) return "0s";
  const sec = Math.ceil(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
}

function RetryCountdown({ nextRetryAt, attemptCount, onElapsed, draftId }) {
  const target = useMemo(() => {
    const t = new Date(nextRetryAt).getTime();
    return Number.isFinite(t) ? t : 0;
  }, [nextRetryAt]);
  const [now, setNow] = useState(() => Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [target]);

  useEffect(() => {
    if (target === 0) return;
    if (!firedRef.current && now >= target) {
      firedRef.current = true;
      // Slight delay so the backend has time to flip in_flight on its side.
      setTimeout(() => onElapsed?.(), 1500);
    }
  }, [now, target, onElapsed]);

  if (target === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-medium text-[#8A6B00]"
        data-testid={`retry-countdown-${draftId}`}
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Attempt {attemptCount + 1} in progress…
      </span>
    );
  }

  const remaining = target - now;
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-medium text-[#8A6B00]"
      data-testid={`retry-countdown-${draftId}`}
    >
      <Clock className="w-3 h-3" />
      Next retry in {fmtCountdown(remaining)}
      <span className="text-[10px] text-[#8A6B00]/70 ml-1">
        ({attemptCount}/3)
      </span>
    </span>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return s; }
}

/** Pixel threshold below which we flip the popover anchor to the right.
 *  Matches the popover's natural width (260px) plus a small breathing buffer
 *  so it never clips off the viewport's right edge. */
const POPOVER_RIGHT_EDGE_THRESHOLD_PX = 280;

/** Small click-to-toggle pill that surfaces "this orphan absorbed
 *  AVIDA-XXX" so coordinators can spot complex merge histories at a
 *  glance without opening the audit drawer. Click toggles a tiny
 *  details popover anchored under the pill — flips horizontal anchor
 *  when within POPOVER_RIGHT_EDGE_THRESHOLD_PX of the right viewport
 *  edge so the popover never clips off-screen. */
function MergedFromPill({ row }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState("left");
  const btnRef = useRef(null);
  if (!row?.merged_from_draft_id) return null;

  const toggle = (e) => {
    e.stopPropagation();
    setOpen((v) => {
      const willOpen = !v;
      if (willOpen && btnRef.current) {
        // Decide anchor based on remaining space at the right edge.
        const rect = btnRef.current.getBoundingClientRect();
        const rightSpace = window.innerWidth - rect.left;
        setAnchor(rightSpace < POPOVER_RIGHT_EDGE_THRESHOLD_PX ? "right" : "left");
      }
      return willOpen;
    });
  };

  return (
    <span className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full border border-[#B5D9C3] bg-[#E9F5EE] text-[#0E6B3F] hover:bg-[#D5EBDD] transition-colors"
        data-testid={`merged-from-pill-${row.draft_id}`}
        title="Click for absorbed-seed details"
      >
        <Link2 className="w-2.5 h-2.5" /> merged
      </button>
      {open && (
        <div
          className={[
            "absolute z-30 mt-1 w-[260px] rounded-lg border border-[#B5D9C3] bg-white shadow-xl px-3 py-2.5 text-left",
            anchor === "right" ? "right-0" : "left-0",
          ].join(" ")}
          data-testid={`merged-from-popover-${row.draft_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-[10.5px] uppercase tracking-[0.12em] font-semibold text-[#0E6B3F] mb-1">
            Absorbed seed
          </div>
          <div className="font-mono text-[11.5px] text-[#0B1E3A] break-all">
            {row.merged_from_draft_id}
          </div>
          <div className="text-[11px] text-[#3A5275] mt-1">
            Was status:{" "}
            <span className="font-semibold text-[#0B1E3A]">
              {row.merged_from_status || "—"}
            </span>
          </div>
          <div className="text-[11px] text-[#3A5275] mt-0.5">
            Merged: {fmtDate(row.merged_at)}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="text-[10.5px] text-[#0A3D79] hover:underline mt-2"
            data-testid={`merged-from-popover-close-${row.draft_id}`}
          >
            Close
          </button>
        </div>
      )}
    </span>
  );
}

export default function DraftsPending() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [includeComplete, setIncludeComplete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [retrying, setRetrying] = useState({});  // { [draft_id]: true }

  const load = React.useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const res = await fetch(
        `${API}/intake/drafts/pending?include_complete=${includeComplete}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setRows(body.drafts || []);
    } catch (e) {
      setErr(e.message || "Failed to load drafts");
      setRows([]);
    } finally {
      setBusy(false);
    }
  }, [includeComplete]);

  useEffect(() => { load(); }, [load]);

  const retryRow = React.useCallback(async (draftId) => {
    setRetrying((m) => ({ ...m, [draftId]: true }));
    try {
      const res = await fetch(
        `${API}/intake/drafts/${draftId}/retry-completion`,
        { method: "POST", headers: { "Content-Type": "application/json" } },
      );
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      toast.success("Retry queued — checking status…");
      // Optimistic refresh so the UI flips out of 'sync failed' immediately.
      load();
    } catch (e) {
      toast.error(`Retry failed: ${e.message || "unknown"}`);
    } finally {
      setRetrying((m) => ({ ...m, [draftId]: false }));
    }
  }, [load]);

  // Auto-poll every 5s while any row has an in-flight retry chain so
  // the UI keeps catching status flips without user clicks.
  const hasInFlight = useMemo(
    () => (rows || []).some((r) => r.in_flight),
    [rows],
  );
  useEffect(() => {
    if (!hasInFlight) return undefined;
    const id = setInterval(() => { load(); }, 5000);
    return () => clearInterval(id);
  }, [hasInFlight, load]);

  const grouped = useMemo(() => {
    const by = { seeded: [], claimed: [], failed: [], done: [], orphan: [] };
    for (const r of rows || []) {
      if (r.sync_failed) by.failed.push(r);
      else if (r.status === "completed_standalone") by.orphan.push(r);
      else if (r.status === "seeded") by.seeded.push(r);
      else if (r.status === "claimed") by.claimed.push(r);
      else by.done.push(r);
    }
    return by;
  }, [rows]);

  const [reconcileTarget, setReconcileTarget] = useState(null);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [auditTarget, setAuditTarget] = useState(null);
  const [paOutcomeTarget, setPaOutcomeTarget] = useState(null);
  const [showLegacyCleanup, setShowLegacyCleanup] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const submitReconcile = async ({ pipelineId, demographicsId, fireAvidaCompletion }) => {
    if (!reconcileTarget) return;
    const draftId = reconcileTarget.draft_id;
    const res = await fetch(
      `${API}/intake/drafts/${encodeURIComponent(draftId)}/reconcile`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pipeline_id: pipelineId,
          demographics_id: demographicsId,
          fire_avida_completion: fireAvidaCompletion,
        }),
      },
    );
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      // Surface the FastAPI `detail` field cleanly so the modal shows
      // "Pipeline X already has a completed assessment" instead of the
      // raw JSON envelope.
      let friendly = "";
      try {
        const parsed = JSON.parse(text);
        friendly = parsed?.detail || "";
      } catch {
        friendly = text;
      }
      throw new Error(friendly || `HTTP ${res.status}`);
    }
    toast.success("Reconciled — joined the Avida pipeline");
    setReconcileTarget(null);
    load();
  };

  const submitReassign = async ({ newRnEmail, reason }) => {
    if (!reassignTarget) return;
    const draftId = reassignTarget.draft_id;
    const res = await fetch(
      `${API}/intake/drafts/${encodeURIComponent(draftId)}/reassign`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_rn_email: newRnEmail, reason }),
      },
    );
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let friendly = "";
      try { friendly = JSON.parse(text)?.detail || ""; }
      catch { friendly = text; }
      throw new Error(friendly || `HTTP ${res.status}`);
    }
    toast.success(`Reassigned to ${newRnEmail}`);
    setReassignTarget(null);
    load();
  };

  const submitPaOutcome = async (payload) => {
    if (!paOutcomeTarget) return;
    const draftId = paOutcomeTarget.draft_id;
    const res = await fetch(
      `${API}/intake/drafts/${encodeURIComponent(draftId)}/pa-approval`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let friendly = "";
      try { friendly = JSON.parse(text)?.detail || ""; }
      catch { friendly = text; }
      throw new Error(friendly || `HTTP ${res.status}`);
    }
    toast.success(
      payload.outcome === "approved"
        ? "PA approval recorded — Final Care Plan generated"
        : "PA denial recorded",
    );
    setPaOutcomeTarget(null);
    load();
  };

  return (
    <main className="min-h-screen bg-[#F7FAFD]">
      <header className="bg-[#0B1E3A] text-white">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#8FB6D9] font-semibold mb-1">
              Avida Care integration
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold" data-testid="pending-title">
              Intake drafts
            </h1>
          </div>
          <div className="flex gap-2">
            <Link to="/" className="btn-ghost" data-testid="pending-home">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
            <Link
              to="/drafts/pa-outcomes"
              className="btn-ghost inline-flex items-center gap-1.5"
              data-testid="pending-pa-outcomes-link"
              title="See every recorded PA outcome and Avida sync status"
            >
              <ShieldCheck className="w-4 h-4" /> PA outcomes
            </Link>
            <button
              onClick={() => setShowActivity((v) => !v)}
              className="btn-ghost inline-flex items-center gap-1.5"
              data-testid="pending-activity-btn"
              title="View recent admin activity (cleanups, reassigns, reconciles)"
            >
              <History className="w-4 h-4" /> Activity
            </button>
            <button
              onClick={() => setShowLegacyCleanup(true)}
              disabled={busy}
              className="btn-ghost inline-flex items-center gap-1.5"
              data-testid="pending-legacy-cleanup-btn"
              title="Purge Phase-1 Margaret Henderson sample data (preview/staging only)"
            >
              <Trash2 className="w-4 h-4" /> Sweep legacy
            </button>
            <button
              onClick={load}
              disabled={busy}
              className="btn-ghost inline-flex items-center gap-1.5"
              data-testid="pending-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {grouped.orphan.length > 0 && (
          <section
            className="rounded-lg border border-[#F0E29A] bg-[#FFFDEC] p-4"
            data-testid="orphan-banner"
          >
            <div className="flex items-start gap-3">
              <FileWarning className="w-5 h-5 text-[#8A6B00] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-[#0B1E3A]">
                  {grouped.orphan.length} standalone intake{grouped.orphan.length > 1 ? "s" : ""} awaiting reconcile
                </div>
                <div className="text-[13px] text-[#8A6B00] mt-1">
                  Captured in the field without an upstream assignment. Open each row, copy the matching
                  parent-app pipeline ID, and click <span className="font-semibold">Reconcile</span> to join
                  the Avida pipeline. PDFs are already generated and downloadable.
                </div>
              </div>
            </div>
          </section>
        )}

        {grouped.failed.length > 0 && (
          <section
            className="rounded-lg border border-[#E6B8B8] bg-[#FBECEC] p-4"
            data-testid="sync-failed-banner"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#A42A2A] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-[#A42A2A]">
                  {grouped.failed.length} assessment{grouped.failed.length > 1 ? "s" : ""} did not sync to Avida Care
                </div>
                <div className="text-[13px] text-[#6B3A3A] mt-1">
                  Documents were generated locally but the outbound notification failed after 3 retries.
                  Please retrieve the documents manually from the Submission Log and upload them to Avida Care
                  until the integration is restored.
                </div>
              </div>
            </div>
          </section>
        )}

        <div className="card-surface">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-xl font-semibold text-[#0B1E3A]">
                Pending intake drafts ({rows ? rows.length : "—"})
              </h2>
              <p className="text-[13px] text-[#3A5275]">
                Assessments requested by Avida Care coordinators. Click a row to open the wizard for that client.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 text-[13px] text-[#3A5275]">
              <input
                type="checkbox"
                className="rounded border-[#B7CADA]"
                checked={includeComplete}
                onChange={(e) => setIncludeComplete(e.target.checked)}
                data-testid="pending-include-complete"
              />
              Include completed / submitted
            </label>
          </div>

          {err && (
            <div className="p-3 rounded bg-[#FBECEC] text-[#A42A2A] text-[13px] mb-4" data-testid="pending-error">
              {err}
            </div>
          )}

          {rows === null ? (
            <div className="text-center py-12 text-[#3A5275]" data-testid="pending-loading">Loading drafts…</div>
          ) : rows.length === 0 ? (
            <div
              className="text-center py-12 border border-dashed border-[#D6E3EF] rounded-lg text-[#3A5275]"
              data-testid="pending-empty"
            >
              <Inbox className="w-8 h-8 mx-auto mb-2 text-[#8FB6D9]" />
              No intake drafts yet.
              <div className="text-[12px] mt-1">Coordinators in Avida Care can pre-seed assessments here.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-[#F0F6FC] text-[#3A5275] text-[11px] uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2">Client</th>
                    <th className="text-left px-3 py-2">Source</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Retry / Reconcile</th>
                    <th className="text-left px-3 py-2">Pipeline ID</th>
                    <th className="text-left px-3 py-2">Assigned RN</th>
                    <th className="text-left px-3 py-2">Seeded</th>
                    <th className="text-left px-3 py-2">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.draft_id}
                      className="border-t border-[#E5EEF5] hover:bg-[#F0F6FC] cursor-pointer transition-colors"
                      data-testid={`draft-row-${r.draft_id}`}
                    >
                      <td className="px-3 py-3">
                        <Link
                          to={`/wizard?draft=${r.draft_id}&source=avida_care`}
                          className="text-[#0B1E3A] font-semibold hover:text-[#1565B8]"
                        >
                          {r.client_name || "(unnamed client)"}
                        </Link>
                      </td>
                      <td className="px-3 py-3">
                        {r.source === "standalone" ? (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[#F0E29A] bg-[#FFFDEC] text-[#8A6B00]"
                            data-testid={`source-badge-${r.draft_id}`}
                          >
                            <FileWarning className="w-3 h-3" /> Standalone
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[#B7CADA] bg-[#EEF5FC] text-[#0A3D79]"
                            data-testid={`source-badge-${r.draft_id}`}
                          >
                            Avida Care
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col items-start gap-1.5">
                          <StatusBadge status={r.status} syncFailed={r.sync_failed} inFlight={r.in_flight} />
                          {r.pa_outcome === "approved" && (
                            <span
                              className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full border border-[#3a8f5a] bg-[#E6F5EC] text-[#1e5a36]"
                              data-testid={`pa-outcome-pill-approved-${r.draft_id}`}
                              title={r.pa_recorded_at ? `Recorded ${fmtDate(r.pa_recorded_at)}` : ""}
                            >
                              <ShieldCheck className="w-3 h-3" /> PA Approved
                            </span>
                          )}
                          {r.pa_outcome === "denied" && (
                            <span
                              className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full border border-[#C05746] bg-[#FBEAE5] text-[#8A2E1A]"
                              data-testid={`pa-outcome-pill-denied-${r.draft_id}`}
                              title={r.pa_recorded_at ? `Recorded ${fmtDate(r.pa_recorded_at)}` : ""}
                            >
                              <XCircle className="w-3 h-3" /> PA Denied
                            </span>
                          )}
                          {r.status === "submitted_to_avida" && !r.pa_outcome && r.job_id && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setPaOutcomeTarget(r); }}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#0A3D79] text-[#0A3D79] bg-white hover:bg-[#EEF5FC] text-[10.5px] font-semibold transition-colors"
                              data-testid={`pa-outcome-btn-${r.draft_id}`}
                              title="Record the ForwardHealth PA approval/denial for this draft"
                            >
                              <FileCheck className="w-3 h-3" /> Record PA outcome
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {r.in_flight ? (
                          <RetryCountdown
                            draftId={r.draft_id}
                            nextRetryAt={r.next_retry_at}
                            attemptCount={r.attempt_count}
                            onElapsed={load}
                          />
                        ) : r.sync_failed ? (
                          <div
                            className="inline-flex flex-col gap-1 text-[11px] text-[#A42A2A]"
                            data-testid={`retry-failed-${r.draft_id}`}
                          >
                            <span className="font-semibold">
                              {r.attempt_count || 3}/3 attempts failed
                            </span>
                            {r.last_error && (
                              <span className="text-[10px] text-[#6B3A3A] truncate max-w-[180px]">
                                {r.last_error}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); retryRow(r.draft_id); }}
                              disabled={!!retrying[r.draft_id]}
                              className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full border border-[#A42A2A] text-[#A42A2A] bg-white hover:bg-[#FBECEC] disabled:opacity-60 disabled:cursor-not-allowed text-[10px] font-semibold w-fit transition-colors"
                              data-testid={`retry-now-${r.draft_id}`}
                              title="Re-fire the outbound webhook to Avida Care"
                            >
                              {retrying[r.draft_id] ? (
                                <><Loader2 className="w-3 h-3 animate-spin" /> Re-queuing…</>
                              ) : (
                                <><RotateCw className="w-3 h-3" /> Retry now</>
                              )}
                            </button>
                          </div>
                        ) : r.status === "completed_standalone" ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setReconcileTarget(r); }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#0A3D79] text-[#0A3D79] bg-white hover:bg-[#EEF5FC] text-[11px] font-semibold transition-colors"
                            data-testid={`reconcile-now-${r.draft_id}`}
                            title="Stamp this orphan with a parent-app pipeline ID"
                          >
                            <Link2 className="w-3 h-3" /> Reconcile
                          </button>
                        ) : (
                          <span className="text-[12px] text-[#8FB6D9]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 font-mono text-[12px] text-[#3A5275]">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="break-all">{r.pipeline_id}</span>
                          <MergedFromPill row={r} />
                        </div>
                      </td>
                      <td className="px-3 py-3 text-[#3A5275]">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate max-w-[160px]" title={r.assigned_rn || ""}>
                            {r.assigned_rn || "—"}
                          </span>
                          {r.status !== "submitted_to_avida" && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setReassignTarget(r); }}
                              className="p-1 rounded text-[#3A5275] hover:text-[#0A3D79] hover:bg-[#EEF5FC] transition-colors"
                              data-testid={`reassign-btn-${r.draft_id}`}
                              title="Reassign to a different RN"
                            >
                              <UserCog className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setAuditTarget(r); }}
                            className="p-1 rounded text-[#3A5275] hover:text-[#0A3D79] hover:bg-[#EEF5FC] transition-colors"
                            data-testid={`audit-btn-${r.draft_id}`}
                            title="View reassignment audit trail"
                          >
                            <History className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-[#3A5275]">{fmtDate(r.created_at)}</td>
                      <td className="px-3 py-3 text-[#3A5275]">{fmtDate(r.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <ReconcileModal
        open={!!reconcileTarget}
        draft={reconcileTarget}
        onClose={() => setReconcileTarget(null)}
        onConfirm={submitReconcile}
      />
      <ReassignModal
        open={!!reassignTarget}
        draft={reassignTarget}
        onClose={() => setReassignTarget(null)}
        onConfirm={submitReassign}
      />
      <AuditDrawer
        open={!!auditTarget}
        draft={auditTarget}
        onClose={() => setAuditTarget(null)}
      />
      <PaOutcomeModal
        open={!!paOutcomeTarget}
        draft={paOutcomeTarget}
        onClose={() => setPaOutcomeTarget(null)}
        onConfirm={submitPaOutcome}
      />
      <LegacyCleanupModal
        open={showLegacyCleanup}
        onClose={() => setShowLegacyCleanup(false)}
        onApplied={load}
      />
      <AdminActivityWidget
        open={showActivity}
        onClose={() => setShowActivity(false)}
      />
    </main>
  );
}
